import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Spinner,
} from '@patternfly/react-core';
import { SyncIcon } from '@patternfly/react-icons';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import {
  PushDeliveryHealthResponse,
  parsePushDeliveryHealthResponse,
} from 'src/contracts/reporting';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_INTERVAL_MS = 3000;
const BACKLOG_WARNING_AGE_MS = 60_000;
const BACKLOG_CRITICAL_AGE_MS = 300_000;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString();
};

const formatNumber = (value?: number | null, fractionDigits = 2) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0,
  });
};

const buildWorkerStatusLabel = (health: PushDeliveryHealthResponse | null) => {
  if (!health) {
    return { color: 'blue' as const, text: 'Loading worker health' };
  }
  if (health.worker_is_stale) {
    return { color: 'red' as const, text: 'Worker stale' };
  }
  return { color: 'green' as const, text: 'Worker healthy' };
};

const buildBacklogStatusLabel = (health: PushDeliveryHealthResponse | null) => {
  if (!health) {
    return { color: 'blue' as const, text: 'Loading backlog' };
  }

  const oldestJobAgeMs = health.oldest_job_age_ms ?? 0;
  if (oldestJobAgeMs >= BACKLOG_CRITICAL_AGE_MS) {
    return { color: 'red' as const, text: 'Backlog critical' };
  }
  if (oldestJobAgeMs >= BACKLOG_WARNING_AGE_MS) {
    return { color: 'orange' as const, text: 'Backlog warning' };
  }
  if (health.queued_jobs > 0) {
    return { color: 'green' as const, text: 'Backlog moving' };
  }
  return { color: 'blue' as const, text: 'Backlog empty' };
};

const buildDeliveryStatusLabel = (health: PushDeliveryHealthResponse | null) => {
  if (!health) {
    return { color: 'blue' as const, text: 'Loading delivery health' };
  }
  if (health.recent_attempted_total === 0) {
    return { color: 'blue' as const, text: 'No recent attempts' };
  }
  const successRate = health.recent_success_rate ?? 0;
  if (successRate < 0.8) {
    return { color: 'red' as const, text: 'Delivery failures elevated' };
  }
  if (successRate < 0.95) {
    return { color: 'orange' as const, text: 'Delivery failures rising' };
  }
  return { color: 'green' as const, text: 'Delivery healthy' };
};

const percentageLabel = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return `${formatNumber(value * 100)}%`;
};

const PushDeliveryMonitor = () => {
  const authedFetch = useAuthedFetch();
  const [health, setHealth] = useState<PushDeliveryHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading((current) => current || !health);
    try {
      const response = await authedFetch(`${API_BASE}/api/reporting/forwarding/push-delivery`);
      if (!response.ok) {
        throw new Error(`Push delivery metrics request failed (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parsePushDeliveryHealthResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      setHealth(parsed.value);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load push delivery metrics');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, health]);

  useEffect(() => {
    void fetchHealth();
    const interval = window.setInterval(() => {
      void fetchHealth();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [fetchHealth]);

  const workerStatus = useMemo(() => buildWorkerStatusLabel(health), [health]);
  const backlogStatus = useMemo(() => buildBacklogStatusLabel(health), [health]);
  const deliveryStatus = useMemo(() => buildDeliveryStatusLabel(health), [health]);

  return (
    <section className="forwarding-section">
      <Card>
        <CardBody>
          <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem>
              <CardTitle>Queue Push Delivery</CardTitle>
            </FlexItem>
            <FlexItem>
              <Button onClick={() => void fetchHealth()} icon={<SyncIcon />} isDisabled={loading}>
                Refresh
              </Button>
            </FlexItem>
            <FlexItem>
              <Label color="blue">Polling every {POLL_INTERVAL_MS / 1000}s</Label>
            </FlexItem>
            <FlexItem>
              <Label color={workerStatus.color}>{workerStatus.text}</Label>
            </FlexItem>
            <FlexItem>
              <Label color={backlogStatus.color}>{backlogStatus.text}</Label>
            </FlexItem>
            <FlexItem>
              <Label color={deliveryStatus.color}>{deliveryStatus.text}</Label>
            </FlexItem>
          </Flex>
        </CardBody>
      </Card>

      {error && (
        <Alert isInline variant="danger" title="Failed to load push delivery metrics">
          {error}
        </Alert>
      )}

      {loading && !health && (
        <Bullseye style={{ minHeight: 120 }}>
          <Spinner />
        </Bullseye>
      )}

      {health && (
        <Grid hasGutter className="forwarding-monitor-grid">
          <GridItem span={4}>
            <Card>
              <CardTitle>Worker Health</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Last seen</span>
                  <strong>{formatDateTime(health.worker_last_seen_at)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Stale after</span>
                  <strong>{formatNumber(health.worker_stale_after_seconds, 0)}s</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Processed</span>
                  <strong>{health.processed_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last processed</span>
                  <strong>{formatDateTime(health.last_processed_at)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last kind</span>
                  <strong>{health.last_kind || '—'}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last outcome</span>
                  <strong>{health.last_outcome || '—'}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={4}>
            <Card>
              <CardTitle>Queue Backlog</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Queued jobs</span>
                  <strong>{health.queued_jobs.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Pending jobs</span>
                  <strong>{health.pending_jobs.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Oldest job age</span>
                  <strong>{formatNumber(health.oldest_job_age_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last lag</span>
                  <strong>{formatNumber(health.last_lag_ms)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last stream</span>
                  <strong>{health.last_stream || '—'}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last stream ID</span>
                  <strong>{health.last_stream_id || '—'}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={4}>
            <Card>
              <CardTitle>Recent Delivery Window</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Window</span>
                  <strong>{health.recent_window_minutes} minutes</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Total rows</span>
                  <strong>{health.recent_deliveries_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Attempted</span>
                  <strong>{health.recent_attempted_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Sent</span>
                  <strong>{health.recent_sent_total.toLocaleString()}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Failures</span>
                  <strong>
                    {(
                      health.recent_failed_total + health.recent_invalid_subscription_total
                    ).toLocaleString()}
                  </strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Success rate</span>
                  <strong>{percentageLabel(health.recent_success_rate)}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Recent Outcomes</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Outcome</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.outcome_counts.map((item) => (
                      <tr key={item.outcome}>
                        <td>{item.outcome}</td>
                        <td>{item.count.toLocaleString()}</td>
                      </tr>
                    ))}
                    {health.outcome_counts.length === 0 && (
                      <tr>
                        <td colSpan={2}>No worker outcome counters yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Delivery Status Breakdown</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.status_breakdown.map((item) => (
                      <tr key={item.status}>
                        <td>{item.status}</td>
                        <td>{item.count.toLocaleString()}</td>
                      </tr>
                    ))}
                    {health.status_breakdown.length === 0 && (
                      <tr>
                        <td colSpan={2}>No recent delivery rows.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Notification Kind Breakdown</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.notification_kind_breakdown.map((item) => (
                      <tr key={item.kind}>
                        <td>{item.kind}</td>
                        <td>{item.count.toLocaleString()}</td>
                      </tr>
                    ))}
                    {health.notification_kind_breakdown.length === 0 && (
                      <tr>
                        <td colSpan={2}>No recent notification rows.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card>
              <CardTitle>Provider Status Codes</CardTitle>
              <CardBody>
                <table className="forwarding-table">
                  <thead>
                    <tr>
                      <th>Status code</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.provider_status_breakdown.map((item) => (
                      <tr key={item.provider_status_code}>
                        <td>{item.provider_status_code}</td>
                        <td>{item.count.toLocaleString()}</td>
                      </tr>
                    ))}
                    {health.provider_status_breakdown.length === 0 && (
                      <tr>
                        <td colSpan={2}>No recent provider status codes.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={12}>
            <Card>
              <CardTitle>Recent Delivery Timestamps</CardTitle>
              <CardBody>
                <div className="forwarding-stat-row">
                  <span>Last sent</span>
                  <strong>{formatDateTime(health.last_sent_at)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last failed</span>
                  <strong>{formatDateTime(health.last_failed_at)}</strong>
                </div>
                <div className="forwarding-stat-row">
                  <span>Last invalid subscription</span>
                  <strong>{formatDateTime(health.last_invalid_subscription_at)}</strong>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      )}
    </section>
  );
};

export default PushDeliveryMonitor;
